import { Component, OnInit, ViewChild } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/models/User';
import { Page } from 'src/app/models/page.model';
import { MatDialog } from '@angular/material/dialog';
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    FormsModule,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
    ITEM_COUNT,
    OFFSET_START,
    SEARCH_DEBOUNCE_MS,
} from 'src/app/constants/searchConstants';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { LocationService } from 'src/app/services/location.service';
import { Employee } from 'src/app/models/Employee';
import { Location } from 'src/app/models/location.model';
import { LocationCategories } from 'src/app/models/LocationCategories';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
    MatOptionSelectionChange,
    MatOptionModule,
} from '@angular/material/core';
import { ACTIVE } from 'src/app/constants/statusConstant';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { NgFor } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Params } from '@angular/router';
import {
    COUNT,
    LOCATIONID,
    NAME,
    OFFSET,
    STATUS,
} from 'src/app/constants/queryParamConstants';

interface IFilterFormControls {
    search: FormControl<string | null>;
    location: FormControl<string | null>;
}

@Component({
    selector: 'app-employee-directory',
    templateUrl: './employee-directory.component.html',
    styleUrls: ['./employee-directory.component.scss'],
    imports: [
        MatToolbarModule,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule,
        FormsModule,
        NgFor,
        MatOptionModule,
        MatInputModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatTooltipModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatCardModule,
        MatSort,
    ],
})
export class EmployeeDirectoryComponent implements OnInit {
    displayedColumns = ['lastName', 'email', 'locationName'];
    suggestNameArr: User[];
    Employees = new Page<Employee>({
        countRequested: 10,
        offsetRequested: 0,
        items: [],
        totalCount: 0,
    });
    dataSource: MatTableDataSource<Employee>;
    @ViewChild(MatSort) sort: MatSort;
    Locations: Location[];
    Location: Location;
    officeLocations: LocationCategories[] = [];
    queryParams = new Map<string, string | number | null>();
    selectedLocationId: string;

    pageIndex = 0;
    pageCount = 20;

    filterForm: FormGroup<IFilterFormControls> = new FormGroup({
        search: new FormControl(),
        location: new FormControl(),
    });

    constructor(
        private userService: UserService,
        public detailsDialog: MatDialog,
        public locationService: LocationService,
        private activatedRoute: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this.locationService.get([]).subscribe((response: Page<Location>) => {
            this.Locations = response.items;
            const defaultOption = {
                Name: 'All Locations',
                Id: '',
            };
            this.officeLocations.push(defaultOption);
            for (const location of this.Locations) {
                const locationCategories = {
                    Name: location.name,
                    Id: location.id,
                };
                this.officeLocations.push(locationCategories);
            }
        });

        this.populateFilters();
        this.getUsers();

        this.filterForm.controls.search.valueChanges
            .pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged())
            .subscribe((response) => {
                const queryParams = new Map<string, string | number | null>([
                    [NAME, response],
                    [COUNT, ITEM_COUNT],
                    [STATUS, ACTIVE],
                ]);
                this.userService
                    .get([], queryParams)
                    .subscribe((nameArr: Page<User>) => {
                        this.suggestNameArr = nameArr.items;
                    });
            });
    }

    setQueryParams(): void {
        this.queryParams.set(STATUS, ACTIVE);
        this.queryParams.set(COUNT, this.pageCount);

        if (this.pageIndex) {
            this.queryParams.set(OFFSET, this.pageIndex * this.pageCount);
        } else {
            this.queryParams.set(OFFSET, OFFSET_START);
        }
    }

    getUsers(): void {
        this.setQueryParams();

        this.userService
            .get([], this.queryParams)
            .subscribe((result: Page<Employee>) => {
                this.Employees = result;
                this.dataSource = new MatTableDataSource(this.Employees.items);
                this.dataSource.sort = this.sort;
                for (const employee of this.Employees.items) {
                    this.Location = this.Locations.find(
                        (location) => location.id === employee.locationId,
                    );

                    if (this.Location !== null && this.Location !== undefined) {
                        employee.locationName = this.Location.name;
                    }
                }
            });
    }

    private populateFilters(): void {
        const params: Params = this.activatedRoute.snapshot.queryParams;

        if (NAME in params) {
            this.filterForm.controls.search.setValue(params[NAME] as string);
        }
    }

    changePage(event: PageEvent): void {
        this.changePageIndex(event.pageIndex);
    }

    changePageIndex(index: number) {
        this.pageIndex = index;
        this.getUsers();
    }

    resetUsers(): void {
        this.queryParams.clear();

        if (this.officeLocations.length > 0) {
            this.selectedLocationId = this.officeLocations[0].Id;
        }
        this.filterForm.controls.search.reset();

        this.changePageIndex(0);
    }

    userSelectionChange(name: string): void {
        this.queryParams.set(NAME, name);
        this.changePageIndex(0);
    }

    locationFilterChange(id: string, event: MatOptionSelectionChange): void {
        if (event.isUserInput) {
            if (id == null) {
                this.queryParams.delete(LOCATIONID);
            } else {
                this.queryParams.set(LOCATIONID, id);
            }

            this.changePageIndex(0);
        }
    }
}
